import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Ellipsis, Pencil, X } from 'lucide-react';
import { renameWorkspace } from '../../lib/workspaces';
import { useTranslation } from '../../lib/i18n';
import { IconButton, OverflowMenu } from './ui';
import { useGenericBi } from '../../lib/use-generic-bi';
import { useNarrow } from '../../lib/use-narrow';
import styles from './WorkspaceNameEditor.module.css';

/** What the header can ask of the open field. Used by the badge's image menu, which finishes an
 *  edit the field itself can't see the end of. */
export interface WorkspaceNameEditorHandle {
  /** Commit the pending name (if any) and close — the same thing clicking away does. */
  commit: () => void;
  /** Put the caret back in the field. No-op once it has closed. */
  focus: () => void;
}

export const WorkspaceNameEditor = forwardRef<
  WorkspaceNameEditorHandle,
  {
    id?: string;
    name: string;
    page: string;
    /** Fires when the field opens/closes, so the header can put the thumbnail into its
     *  matching edit state (Figma 11246-166792 draws the scrim + pencil on the avatar). */
    onEditingChange?: (editing: boolean) => void;
    /**
     * Stop blur from committing and closing the field.
     *
     * Set while the badge's file picker is open. Opening the OS file dialog takes focus off the
     * page, so the field blurs — and blur is what commits and closes it. Without this the whole
     * edit state disappeared the instant "Upload a photo" was clicked, before the user had even
     * chosen a file.
     */
    suspendBlurCommit?: boolean;
    /**
     * The Space card's header (`symphonySpaceCardV2`, Space rules spec 2026-10-29): a ⋯ beside the name that opens the
     * Space's menu — "Rename Space", which starts the same in-place edit as the pencil it replaces. "Delete Space" joins
     * it once its words and what it does to the Space's data are decided (still open in the spec).
     */
    menu?: boolean;
  }
>(function WorkspaceNameEditor({ id, name, page, onEditingChange, suspendBlurCommit, menu = false }, ref) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  // Mobile drops the workspace title to the 32/34 Display-03 scale, matching the standard PageHeader
  // (Figma 4176-6999 / 9849-69753); desktop keeps the 40/44 Display. The field takes the SAME step as
  // the heading it replaces — that is the whole point of editing in place.
  const narrow = useNarrow();
  const titleClass = narrow ? 'sym-h1' : 'sym-display';
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editing) setDraft(name);
  }, [name, editing]);
  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);
  useEffect(() => {
    onEditingChange?.(editing);
    // Only the transition matters; a new callback identity must not re-announce it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  const begin = () => {
    if (!id) return;
    setDraft(name);
    setEditing(true);
    logPageClickBi({ page_name: page, button_name: 'rename_workspace' });
  };
  const cancel = () => {
    setDraft(name);
    setEditing(false);
  };
  const commit = () => {
    const next = draft.trim();
    setEditing(false);
    if (!id || !next || next === name) {
      setDraft(name);
      return;
    }
    setSaving(true);
    renameWorkspace(id, next)
      .catch(() => {
        // Revert to the last-known-good name; the cache was never patched on failure.
        setDraft(name);
      })
      .finally(() => setSaving(false));
  };

  useImperativeHandle(ref, () => ({
    commit,
    focus: () => inputRef.current?.focus(),
  }));

  if (editing) {
    return (
      <div className={`${styles.row} ${styles.editRow}`}>
        <input
          ref={inputRef}
          className={`${titleClass} ${styles.input}`}
          value={draft}
          autoFocus
          disabled={saving}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => {
            // The file dialog's focus theft is not the user leaving the field — see the prop.
            if (suspendBlurCommit) return;
            commit();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              cancel();
            }
          }}
          aria-label={t('common.edit')}
          // Size to the text so the caret sits right after it; `.input` caps it to the hero width.
          style={{ width: `${Math.max(draft.length, 4) + 1}ch` }}
        />
        {/* The glyph the design puts at the end of the underlined row CLEARS the field and keeps
            the caret in it, so the user can type a new name — it is not a cancel (matching
            native's `clearNameButton`). An empty commit is a no-op, so tapping away after
            clearing restores the original name. `preventDefault` on mousedown is what keeps the
            focus: without it the input blurs, and blur commits. */}
        <IconButton
          size="tiny"
          onMouseDown={(e) => {
            e.preventDefault();
            setDraft('');
            inputRef.current?.focus();
          }}
          aria-label={t('workspace.clearName')}
          title={t('workspace.clearName')}
        >
          <X size={16} aria-hidden />
        </IconButton>
      </div>
    );
  }

  return (
    <div className={styles.row}>
      <h1 className={`${titleClass} ${styles.title}`}>{name}</h1>
      {id && menu && (
        <OverflowMenu
          ariaLabel={t('workspace.menu.label')}
          triggerVariant="text"
          triggerIcon={<Ellipsis size={20} aria-hidden />}
          align="start"
          onOpen={() => logPageClickBi({ page_name: page, button_name: 'space_options' })}
          actions={[{ key: 'rename', label: t('workspace.menu.rename'), onSelect: begin }]}
        />
      )}
      {id && !menu && (
        <IconButton size="medium" onClick={begin} aria-label={t('common.edit')} title={t('common.edit')}>
          {/* 16px, matching Figma's `16/pencil`. The glyph it replaced was an 18px box whose path only
              filled ~60% of its viewBox, so it drew ~11px of ink and read as too small. */}
          <Pencil size={16} aria-hidden />
        </IconButton>
      )}
    </div>
  );
});
